import { redirect } from '@tanstack/react-router'
import type { RouterContext } from '@/shared/services'
import { can, type Permission } from './permissions'
import type { Session } from './session-model'
import { fetchSession } from './session'

export const LOGIN_PATH = '/login'

export class ForbiddenError extends Error {
  override readonly name = 'ForbiddenError'
  constructor(readonly permission: Permission) {
    super(`Missing permission: ${permission}`)
  }
}

interface GuardArgs {
  context: RouterContext
  location: { href: string }
}

export function loginHref(redirectTo: string): string {
  return `${LOGIN_PATH}?${new URLSearchParams({ redirect: redirectTo }).toString()}`
}

/** `beforeLoad` guard: redirects to the login page, preserving the requested location. */
export async function requireAuth({ context, location }: GuardArgs): Promise<{ session: Session }> {
  const { queryClient, auth } = context.services
  const session = await fetchSession(queryClient, auth)
  if (!session) throw redirect({ href: loginHref(location.href) })
  return { session }
}

/** `beforeLoad` guard: requires a session and a permission; renders the forbidden state otherwise. */
export function requirePermission(permission: Permission) {
  return async (args: GuardArgs): Promise<{ session: Session }> => {
    const { session } = await requireAuth(args)
    if (!can(session, permission)) throw new ForbiddenError(permission)
    return { session }
  }
}

/** Only same-origin relative paths are accepted as post-login targets (no open redirect). */
export function safeRedirectTarget(target: string | undefined, fallback = '/app'): string {
  if (!target?.startsWith('/') || target.startsWith('//') || target.startsWith(LOGIN_PATH))
    return fallback
  return target
}
